import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { FC, useCallback, useMemo } from 'react'

import { Icon } from '@src/quince-components/src'
import { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { ProductOptionDisplayType } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import OptionSelector from '@src/quince-site-pdp/src/atoms/optionSelector/OptionSelector'
import { getProductPropertiesForTrackingNew } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import FilledStar from '../icons/filledStar.inline.svg'
import { CardVariant, IProductCard, ProductCardReviewDetails } from '../product-card.types'

import { BottomTagComponent } from './bottom-tag'
import styles from './product-card-footer.module.scss'
import { isFooterHiddenForProduct } from './product-card-footer.utils'

export const OPTION_DISPLAY_TYPES_VS_CLASS_NAME = {
  [ProductOptionDisplayType.COLOR]: styles.colorOptionProductCard,
  [ProductOptionDisplayType.IMAGE]: styles.colorOptionProductCard,
}

export interface ProductCardFooterProps {
  productCard: IProductCard
  selectedVariant?: CardVariant
  cardVariants: CardVariant[]
  setSelectedVariant: (cardVariant: CardVariant) => void
  reviewSummary?: ProductCardReviewDetails
  hideReviews?: boolean
  hideOptions?: boolean
  shouldShowMore?: boolean
  optionLimit?: number
  productClickTrackConfig?: TrackConfig
  onShowMoreClick?: (params: { selectedVariant?: CardVariant }) => void
  isWhiteGloveDelivery?: boolean
  eddDetails?: EDDDetails
  ratingTextClassName?: string
  excessOptionsTextClassName?: string
  isPageEligibleForHolidayTag?: boolean
}

// @i18n-ignore
const NO_VARIANT_FOUND_ERROR_MESSAGE = 'No variant found for selected option in product card'
const RADIO_GROUP_ROLE = 'radiogroup'

export const ProductCardFooter: FC<ProductCardFooterProps> = ({
  productCard,
  selectedVariant,
  cardVariants,
  setSelectedVariant,
  reviewSummary,
  hideReviews,
  hideOptions,
  shouldShowMore,
  optionLimit,
  productClickTrackConfig,
  onShowMoreClick,
  isWhiteGloveDelivery,
  eddDetails,
  ratingTextClassName,
  excessOptionsTextClassName,
  isPageEligibleForHolidayTag,
}) => {
  const { t } = useTranslation('common')
  const product = productCard.product
  const formattedAvgRating = getFormattedAverageRating(reviewSummary?.averageRating)

  const optionValueList = useMemo(
    () =>
      cardVariants
        // Dont display text based options in footer
        .filter(cardVariant => cardVariant.displayConfig.displayType !== ProductOptionDisplayType.TEXT)
        .map(cardVariant => {
          return cardVariant.displayConfig
        }),
    [cardVariants],
  )

  const onOptionSelect = useCallback(
    (optionValue: string) => {
      const newSelectedVariant = cardVariants.find(cardVariant => cardVariant.displayConfig.value === optionValue)

      if (!newSelectedVariant) {
        captureException(new Error(NO_VARIANT_FOUND_ERROR_MESSAGE), {
          feature: FEATURES.UPDATE_COLOR_OPTIONS,
          module: MODULES.PRODUCT_CARD,
          extraInfo: {
            // @i18n-ignore
            capturedFrom: 'onOptionSelect in product-card-footer.tsx',
          },
        })
        return
      }
      setSelectedVariant(newSelectedVariant)

      const oldSelectedOptions: ProductVariantOption[] = selectedVariant
        ? [{ name: selectedVariant.displayConfig.name, value: selectedVariant.displayConfig.value }]
        : []
      const tags = newSelectedVariant.tags.map(productTag => productTag.value)
      // @i18n-ignore
      const COLOR_OPTION_NAME = 'Color'
      trackEvent(TELEMETRY_EVENT_NAMES.VARIANT_SELECT, {
        color: newSelectedVariant.displayConfig.value,
        product_id: product.relatedIds.spid,
        internalVariantIds: selectedVariant?.variantIds,
        variantIds: selectedVariant?.svids,
        product_options: oldSelectedOptions,

        // TODO: Do we need this?
        // product_options_all: product.productOptions,
        tags: newSelectedVariant.tags.map(tag => tag.value),
        tag_ids: transformTagsIntoAnalyticsTags(newSelectedVariant.tags.map(tag => tag.value)),
        stock_info: null,
        entity_id: product.productId,
        // @i18n-ignore
        entity_type: 'product_id',
        productType: product.productTypeName,
        productTitle: product.title,
        variantTitle: newSelectedVariant.title,
        price: newSelectedVariant.price?.salePrice,
        is_product_card: true,
        division: product.classification?.division,
        department: product.classification?.department,
        subdepartment: product.classification?.subdepartment,
        product_option_name: COLOR_OPTION_NAME,
        product_option_value: optionValue,
        element_name: productClickTrackConfig?.context?.contentContainerName || null,
        element_display_name: productClickTrackConfig?.context?.contentContainerDisplayName || null,
        element_type: productClickTrackConfig?.context?.contentContainerType || null,
        element_id: productClickTrackConfig?.context?.trackingId || null,
        element_position: productClickTrackConfig?.context?.contentContainerPosition || null,
        context: {
          productProperties: getProductPropertiesForTrackingNew({ product, productVariant: selectedVariant, tags }),
        },
      })
    },
    [
      cardVariants,
      product,
      productClickTrackConfig?.context?.contentContainerDisplayName,
      productClickTrackConfig?.context?.contentContainerName,
      productClickTrackConfig?.context?.contentContainerPosition,
      productClickTrackConfig?.context?.contentContainerType,
      productClickTrackConfig?.context?.trackingId,
      selectedVariant,
      setSelectedVariant,
    ],
  )

  const showMoreOption = shouldShowMore

  const handleShowMoreClick = useCallback(() => {
    onShowMoreClick?.({ selectedVariant: selectedVariant })
  }, [onShowMoreClick, selectedVariant])

  const selectedValue = selectedVariant?.displayConfig.value

  const showOptionFooter = !hideOptions && !isFooterHiddenForProduct(product.productId)
  const optionName = selectedVariant?.displayConfig.name ?? 'Color'

  return (
    <div>
      {!hideReviews && formattedAvgRating && (
        <div className={styles.rating}>
          <Icon className={styles.rating_icon} iconSvg={FilledStar} />
          <span className={classNames(styles.rating_text, ratingTextClassName)}>{formattedAvgRating}</span>
        </div>
      )}
      {showOptionFooter && (
        <div role={RADIO_GROUP_ROLE} aria-label={t('pc_options_aria', { optionName })}>
          {/* TODO: Create new option selector for product card */}
          <OptionSelector
            optionDisplayTypesVsClassName={OPTION_DISPLAY_TYPES_VS_CLASS_NAME}
            optionValueList={optionValueList}
            className={styles.colorOptionWrapper}
            optionWrapperClassName={styles.colorOptionWrapper__optionWrapper}
            selectedValue={selectedValue}
            isAllRoundOptions
            onSelect={onOptionSelect}
            showMoreOption={showMoreOption}
            optionLimit={optionLimit}
            onShowMoreClick={handleShowMoreClick}
            excessOptionsTextClassName={excessOptionsTextClassName}
          />
        </div>
      )}
      <BottomTagComponent
        isWhiteGloveDelivery={isWhiteGloveDelivery}
        eddDetails={eddDetails}
        isPageEligibleForHolidayTag={isPageEligibleForHolidayTag}
      />
    </div>
  )
}
